import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { CalendarIcon } from '@/features/user/components/icons/ProfileIcons';
import CustomDropdown from './CustomDropdown';

interface CalendarPickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  id?: string;
  name?: string;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({ 
  value, 
  onChange, 
  label = "Date of Birth", 
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Month options
  const monthOptions = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' }
  ];
  
  // Year options (last 50 years to next 10 years)
  const yearOptions = Array.from({ length: 60 }, (_, i) => {
    const year = new Date().getFullYear() - 50 + i;
    return { value: year, label: year.toString() };
  });
  
  // Format the date for display
  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return 'Select date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Calendar logic
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  const isDateSelected = (day: number) => {
    if (!value) return false;
    const selectedDate = new Date(value + 'T00:00:00.000Z'); // Parse as UTC to avoid timezone issues
    return selectedDate.getUTCDate() === day && 
           selectedDate.getUTCMonth() === currentMonth.getMonth() && 
           selectedDate.getUTCFullYear() === currentMonth.getFullYear();
  };

  const isDateDisabled = (day: number) => {
    const date = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth(), day));
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    return date > today;
  };

  const handleDateClick = (day: number) => {
    // Create date in UTC to avoid timezone conversion issues
    const date = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth(), day));
    const dateString = date.toISOString().split('T')[0];
    onChange(dateString);
    setIsOpen(false);
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };


  // Set initial month to selected date or current month
  useEffect(() => {
    if (value) {
      const selectedDate = new Date(value);
      setCurrentMonth(selectedDate);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full">
      <label htmlFor={id} className="block text-sm font-medium text-gray-400 mb-2">
        {label}
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-gray-900/50 border border-gray-700 rounded-lg py-2.5 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 transition-all duration-300 flex items-center justify-between hover:bg-gray-800/50"
        >
          <span className={value ? 'text-gray-100' : 'text-gray-500'}>
            {formatDisplayDate(value)}
          </span>
          <CalendarIcon className="w-5 h-5 text-violet-400" />
        </button>
        
        {isOpen && (
          <div className="absolute z-20 w-80 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-3">
              <button 
                type="button"
                onClick={goToPreviousMonth}
                className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronLeftIcon className="w-3 h-3 text-gray-400" />
              </button>
              
              <div className="flex items-center gap-2">
                <CustomDropdown
                  value={currentMonth.getMonth()}
                  onChange={(value) => {
                    const newMonth = new Date(currentMonth.getFullYear(), value as number, 1);
                    setCurrentMonth(newMonth);
                  }}
                  options={monthOptions}
                  className="w-24"
                />
                
                <CustomDropdown
                  value={currentMonth.getFullYear()}
                  onChange={(value) => {
                    const newMonth = new Date(value as number, currentMonth.getMonth(), 1);
                    setCurrentMonth(newMonth);
                  }}
                  options={yearOptions}
                  className="w-20"
                />
              </div>
              
              <button 
                type="button"
                onClick={goToNextMonth}
                className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ChevronRightIcon className="w-3 h-3 text-gray-400" />
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                <div key={day} className="text-center text-xs text-gray-500 py-1 font-medium">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-0.5">
              {emptyDays.map(i => (
                <div key={`empty-${i}`} className="h-7"></div>
              ))}
              {days.map(day => {
                const isSelected = isDateSelected(day);
                const isDisabled = isDateDisabled(day);
                
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => !isDisabled && handleDateClick(day)}
                    disabled={isDisabled}
                    className={`h-7 flex items-center justify-center text-xs rounded transition-all duration-200 ${
                      isSelected
                        ? 'bg-violet-600 text-white font-bold'
                        : isDisabled
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'bg-gray-800 text-white cursor-pointer hover:bg-violet-500/20'
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPicker;
